/**
 * GrowthTab: Admin → User Management → Growth. "Find. Draft. You approve."
 *
 * functions/growth finds betting threads (Reddit, Bluesky, plus leads added by
 * hand for X / Discord / forums), drafts helpful replies and data posts, and
 * checks each draft for 21+ / responsible-gambling and no-profit-promise rules.
 * Nothing posts by itself: Approve copies the text and opens the thread or a
 * prefilled composer; you post it and mark it posted. The goal is beta
 * conversions from growth links (waitlist → approved → signed up → searched).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, ScrollView, ActivityIndicator, Platform, Linking } from 'react-native';
import { getGrowthOverview, growthAction, saveGrowthSettings, runGrowthJob } from '../services/growthService';
import { showToast } from '../utils/toast';
import { A, Pill, Panel, Btn, Segments, EmptyState, Stat, HBars, relTime, shortDate } from './admin/adminUi';
import Appear from './motion/Appear';
import { AV, ADM_DISPLAY } from './floodlight/vars/admin';
import {
    DRAFT_FILTERS, filterDrafts, draftCounts, composerUrl, safeUrl, communityLabel, DATA_MODE_COPY, sourceRows, GOAL_TONE, money, PLATFORM_LABEL,
} from '../utils/growthUi';

const STATUS_COLOR = { ready: A.green, flagged: A.amber, approved: A.blue, posted: A.accent, denied: A.soft, replaced: A.faint };
const LEAD_COLOR = { new: A.green, drafted: A.blue, snoozed: A.amber, dismissed: A.faint, done: A.accent, skipped: A.faint };
const DENY = [['off-topic', 'Off-topic'], ['too-salesy', 'Too salesy'], ['no-links', 'No links here'], ['wrong-facts', 'Wrong facts'], ['tone', 'Tone'], ['duplicate', 'Duplicate']];
const CHAR_LIMITS = { x: 280, bluesky: 300 };

const input = { color: AV.ink, backgroundColor: 'rgba(255,255,255,0.05)', borderWidth: 1, borderColor: AV.line, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, fontSize: 13 };
const label = { color: AV.faint, fontSize: 11, fontWeight: '700', letterSpacing: 0.4 };

async function copyText(text) {
    try {
        if (Platform.OS === 'web' && navigator?.clipboard) { await navigator.clipboard.writeText(text); return true; }
    } catch { /* fall through */ }
    return false;
}
function openUrl(raw) {
    const url = safeUrl(raw);
    if (!url) return;
    if (Platform.OS === 'web') window.open(url, '_blank', 'noopener,noreferrer'); else Linking.openURL(url);
}
const confirmWeb = (msg) => Platform.OS !== 'web' || window.confirm(msg);

function Banner({ tone = 'warn', children }) {
    const c = tone === 'bad' ? A.red : tone === 'info' ? A.blue : A.amber;
    return (
        <View style={{ borderWidth: 1, borderColor: `${c}55`, backgroundColor: `${c}12`, borderRadius: 10, padding: 12 }}>
            <Text style={{ color: AV.ink, fontSize: 13, lineHeight: 19 }}>{children}</Text>
        </View>
    );
}

function DraftCard({ d, busy, act, index }) {
    const [body, setBody] = useState(d.body);
    const [postedUrl, setPostedUrl] = useState('');
    const [denying, setDenying] = useState(false);
    useEffect(() => { setBody(d.body); }, [d.body]);
    const dirty = body !== d.body;
    const blocks = (d.compliance?.issues || []).filter((i) => i.severity === 'block');
    const warns = (d.compliance?.issues || []).filter((i) => i.severity === 'warn');
    const limit = CHAR_LIMITS[d.platform];
    const b = (k) => busy === `${d.id}:${k}`;
    const editable = !['posted', 'denied', 'replaced'].includes(d.status);

    const approve = async () => {
        // Copy and open the tab inside the click (browsers block both after an await).
        const copying = copyText(d.body);
        const url = composerUrl(d);
        const win = Platform.OS === 'web' && url ? window.open('', '_blank') : null;
        const ok = await act(d, 'approve');
        if (!ok) { win?.close(); return; }
        if (win) { win.opener = null; win.location.href = url; } else openUrl(url);
        const copied = await copying;
        showToast(copied ? 'Approved and copied. Paste it in the tab that opened, then mark it posted.' : 'Approved. Copy the text, post it, then mark it posted.', 'success');
    };

    return (
        <Appear index={Math.min(index, 8)} distance={6}>
            <View style={{ borderTopWidth: 1, borderTopColor: AV.line, paddingVertical: 14, gap: 10 }}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    <Pill label={String(d.status).toUpperCase()} color={STATUS_COLOR[d.status] || A.soft} />
                    <Pill label={communityLabel(d)} color={A.blue} />
                    <Pill label={d.kind === 'post' ? 'OUR POST' : 'REPLY'} color={A.soft} />
                    <Pill label={d.generator === 'ai' ? `AI · ${d.ai?.model || ''}` : 'TEMPLATE'} color={A.soft} />
                    {d.dataMode && d.dataMode !== 'live' && <Pill label={`${String(d.dataMode).toUpperCase()} DATA`} color={A.amber} />}
                    {d.promo && <Pill label={d.promo.toUpperCase()} color={A.faint} />}
                    <Text style={{ color: AV.faint, fontSize: 11 }}>{relTime(d.createdAt)}</Text>
                </View>
                {!!d.leadTitle && (
                    <Text style={{ color: AV.soft, fontSize: 12 }} numberOfLines={2}>
                        Thread: <Text style={{ color: AV.ink, fontWeight: '600' }} onPress={() => openUrl(d.leadUrl)} accessibilityRole="link">{d.leadTitle}</Text>
                    </Text>
                )}
                {!!d.title && <Text style={{ color: AV.ink, fontSize: 14, fontWeight: '700' }}>{d.title}</Text>}
                <TextInput
                    value={body} onChangeText={setBody} multiline editable={editable} accessibilityLabel="Draft text"
                    style={[input, { minHeight: d.kind === 'post' && limit ? 90 : 140, lineHeight: 19, textAlignVertical: 'top' }]}
                />
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                    {!!limit && <Text style={{ color: body.length > limit ? A.red : AV.faint, fontSize: 11 }}>{body.length}/{limit}{d.platform === 'x' ? ' (links count as 23)' : ''}</Text>}
                    {d.link && <Text style={{ color: AV.faint, fontSize: 11 }} numberOfLines={1}>Tracking link: {d.link.replace(/^https:\/\//, '')}</Text>}
                </View>
                {blocks.map((i) => <Text key={i.code + i.message} style={{ color: A.red, fontSize: 12 }}>✕ {i.message}</Text>)}
                {warns.map((i) => <Text key={i.code + i.message} style={{ color: A.amber, fontSize: 12 }}>! {i.message}</Text>)}
                {!!d.compliance?.notes && <Text style={{ color: AV.faint, fontSize: 11, lineHeight: 16 }}>{d.compliance.notes}</Text>}
                {!!d.aiError && <Text style={{ color: AV.faint, fontSize: 11 }}>AI failed ({d.aiError}); this is the template draft.</Text>}

                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    {dirty && <Btn small tone="primary" label={b('edit') ? 'Saving…' : 'Save edit'} onPress={() => act(d, 'edit', { body })} />}
                    {d.status === 'ready' && !dirty && <Btn small tone="good" label={b('approve') ? '…' : d.kind === 'reply' ? 'Approve, copy + open thread' : 'Approve, copy + open composer'} onPress={approve} />}
                    {d.status === 'approved' && (
                        <>
                            <TextInput value={postedUrl} onChangeText={setPostedUrl} placeholder="Link to your post (optional)" placeholderTextColor={A.faint} accessibilityLabel="Posted URL" style={[input, { flex: 1, minWidth: 200, paddingVertical: 6 }]} />
                            <Btn small tone="good" label={b('posted') ? '…' : 'I posted it'} onPress={() => act(d, 'posted', { url: postedUrl })} />
                            <Btn small label="Copy again" onPress={async () => showToast((await copyText(d.body)) ? 'Copied' : 'Copy failed: select the text', 'info')} />
                            <Btn small label="Undo approve" onPress={() => act(d, 'reopen')} />
                        </>
                    )}
                    {editable && d.status !== 'approved' && (denying ? (
                        <>
                            <Text style={{ color: AV.faint, fontSize: 11 }}>Why?</Text>
                            {DENY.map(([v, l]) => <Btn key={v} small label={l} onPress={() => { setDenying(false); act(d, 'deny', { reason: v }); }} />)}
                            <Btn small label="Cancel" onPress={() => setDenying(false)} />
                        </>
                    ) : <Btn small tone="danger" label="Deny" onPress={() => setDenying(true)} />)}
                    {editable && d.kind === 'reply' && d.status !== 'approved' && <Btn small label={b('redraft') ? '…' : 'Redraft'} onPress={() => act(d, 'redraft')} />}
                    {d.status === 'posted' && d.postedUrl && <Btn small label="Open post" onPress={() => openUrl(d.postedUrl)} />}
                    {(d.status === 'denied') && <Text style={{ color: AV.faint, fontSize: 11 }}>Denied: {d.denyReason}. Later drafts for this community take it into account.</Text>}
                </View>
            </View>
        </Appear>
    );
}

function LeadRow({ l, busy, act, index }) {
    const b = (k) => busy === `${l.id}:${k}`;
    return (
        <Appear index={Math.min(index, 10)} distance={6}>
            <View style={{ flexDirection: 'row', gap: 12, paddingVertical: 10, borderTopWidth: 1, borderTopColor: AV.line, flexWrap: 'wrap', alignItems: 'center' }}>
                <Text style={{ color: l.score >= 70 ? A.green : l.score >= 50 ? AV.ink : AV.faint, fontSize: 18, fontWeight: '800', width: 40, textAlign: 'right', ...ADM_DISPLAY }} accessibilityLabel={`Score ${l.score}`}>{l.score}</Text>
                <View style={{ flex: 1, minWidth: 240, gap: 3 }}>
                    <Text style={{ color: AV.ink, fontSize: 13, fontWeight: '600' }} onPress={() => openUrl(l.url)} accessibilityRole="link" numberOfLines={2}>{l.title}</Text>
                    <Text style={{ color: AV.faint, fontSize: 11 }} numberOfLines={2}>
                        {communityLabel({ ...l, kind: 'reply' })} · {relTime(l.createdAt)}{l.comments != null ? ` · ${l.comments} replies` : ''} · {(l.reasons || []).join(', ')}{(l.flags || []).length ? ` · ⚑ ${l.flags.join(', ')}` : ''}{l.skipReason ? ` · skipped: ${l.skipReason}` : ''}
                    </Text>
                </View>
                <Pill label={String(l.status).toUpperCase()} color={LEAD_COLOR[l.status] || A.soft} />
                <View style={{ flexDirection: 'row', gap: 6 }}>
                    {['new', 'skipped', 'snoozed'].includes(l.status) && <Btn small tone="primary" label={b('draftLead') ? 'Drafting…' : 'Draft reply'} onPress={() => act(l, 'draftLead')} />}
                    {l.status === 'new' && <Btn small label="Snooze 3d" onPress={() => act(l, 'snoozeLead', { days: 3 })} />}
                    {l.status !== 'dismissed' && l.status !== 'done' && <Btn small tone="danger" label="Dismiss" onPress={() => act(l, 'dismissLead')} />}
                    {(l.status === 'dismissed' || l.status === 'snoozed') && <Btn small label="Restore" onPress={() => act(l, 'restoreLead')} />}
                </View>
            </View>
        </Appear>
    );
}

function AddLead({ onAdded }) {
    const [f, setF] = useState({ platform: 'x', url: '', title: '', text: '', community: '' });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            const r = await growthAction('addLead', f);
            showToast(r.skipped ? `Added. The drafter skipped it: ${r.reason}` : 'Added and drafted. It is in the review queue.', 'success');
            setF((x) => ({ ...x, url: '', title: '', text: '' }));
            onAdded();
        } catch (e) { showToast(e.message, 'error'); } finally { setBusy(false); }
    };
    return (
        <View style={{ gap: 8 }}>
            <Segments value={f.platform} onChange={(platform) => setF((x) => ({ ...x, platform }))} options={['x', 'discord', 'forum', 'reddit', 'bluesky'].map((p) => ({ value: p, label: PLATFORM_LABEL[p] }))} />
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                <TextInput value={f.url} onChangeText={(url) => setF((x) => ({ ...x, url }))} placeholder="https://… link to the post or message" placeholderTextColor={A.faint} accessibilityLabel="Lead URL" style={[input, { flex: 2, minWidth: 240 }]} />
                <TextInput value={f.community} onChangeText={(community) => setF((x) => ({ ...x, community }))} placeholder={f.platform === 'reddit' ? 'subreddit' : 'server / forum (optional)'} placeholderTextColor={A.faint} accessibilityLabel="Community" style={[input, { flex: 1, minWidth: 160 }]} />
            </View>
            <TextInput value={f.text} onChangeText={(text) => setF((x) => ({ ...x, text }))} multiline placeholder="Paste what they wrote, so the reply answers it" placeholderTextColor={A.faint} accessibilityLabel="Lead text" style={[input, { minHeight: 70, textAlignVertical: 'top' }]} />
            <View style={{ flexDirection: 'row' }}><Btn small tone="primary" label={busy ? 'Drafting…' : 'Add + draft reply'} onPress={submit} disabled={busy || !f.url} /></View>
        </View>
    );
}

function SettingsPanel({ settings, onSaved }) {
    const [s, setS] = useState(settings);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setS(settings); }, [settings]);
    const set = (patch) => setS((x) => ({ ...x, ...patch }));
    const setAi = (patch) => setS((x) => ({ ...x, ai: { ...x.ai, ...patch } }));
    const setGoal = (patch) => setS((x) => ({ ...x, goal: { ...x.goal, ...patch } }));
    const numField = (lbl, value, onChange, hint) => (
        <View style={{ gap: 4, minWidth: 140, flexGrow: 1, flexBasis: 140 }}>
            <Text style={label}>{lbl}</Text>
            <TextInput value={String(value ?? '')} onChangeText={onChange} keyboardType="numeric" accessibilityLabel={lbl} style={input} />
            {!!hint && <Text style={{ color: AV.faint, fontSize: 10 }}>{hint}</Text>}
        </View>
    );
    const save = async () => {
        setSaving(true);
        try {
            const out = await saveGrowthSettings({ ...s, queries: typeof s.queries === 'string' ? s.queries.split(',').map((q) => q.trim()).filter(Boolean) : s.queries });
            showToast('Growth settings saved (numbers are clamped to the hard caps)', 'success');
            onSaved(out.settings);
        } catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
    };
    return (
        <Panel title="Settings" subtitle="Schedules run every 3 hours. Cost caps are enforced on the server; the hard ceiling is $10/day.">
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
                <Btn small label={s.enabled ? 'Radar + drafting: on' : 'Radar + drafting: off'} tone={s.enabled ? 'good' : 'default'} onPress={() => set({ enabled: !s.enabled })} />
                <Btn small label={s.ai.enabled ? 'AI drafting: on' : 'AI drafting: off (templates only)'} tone={s.ai.enabled ? 'good' : 'default'} onPress={() => setAi({ enabled: !s.ai.enabled })} />
                <Btn small label="Auto-post: off (no connector yet)" disabled onPress={() => {}} accessibilityLabel="Auto-post is off. No posting connector exists; every post needs your approval." />
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                {numField('AI BUDGET $/DAY', s.ai.dailyBudgetUsd, (v) => setAi({ dailyBudgetUsd: v }))}
                {numField('AI CALLS / RUN', s.ai.maxCallsPerRun, (v) => setAi({ maxCallsPerRun: v }))}
                {numField('AI CALLS / DAY', s.ai.maxCallsPerDay, (v) => setAi({ maxCallsPerDay: v }))}
                {numField('APPROVALS TARGET / DAY', s.dailyTarget, (v) => set({ dailyTarget: v }))}
                {numField('APPROVALS MAX / DAY', s.dailyMax, (v) => set({ dailyMax: v }), 'Approve is refused past this.')}
                {numField('MIN SCORE TO DRAFT', s.autoDraftMinScore, (v) => set({ autoDraftMinScore: v }))}
                {numField('POSTS DRAFTED / RUN', s.maxPostDraftsPerRun, (v) => set({ maxPostDraftsPerRun: v }))}
                {numField('GOAL: GROWTH WAITLIST JOINS', s.goal.target, (v) => setGoal({ target: v }))}
            </View>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                <View style={{ gap: 4, flexGrow: 1, flexBasis: 160 }}><Text style={label}>GOAL START (YYYY-MM-DD)</Text><TextInput value={s.goal.startAt} onChangeText={(v) => setGoal({ startAt: v })} accessibilityLabel="Goal start" style={input} /></View>
                <View style={{ gap: 4, flexGrow: 1, flexBasis: 160 }}><Text style={label}>GOAL END (YYYY-MM-DD)</Text><TextInput value={s.goal.endAt} onChangeText={(v) => setGoal({ endAt: v })} accessibilityLabel="Goal end" style={input} /></View>
            </View>
            <View style={{ gap: 4 }}>
                <Text style={label}>BLUESKY SEARCHES (comma separated)</Text>
                <TextInput value={Array.isArray(s.queries) ? s.queries.join(', ') : s.queries} onChangeText={(v) => set({ queries: v })} accessibilityLabel="Bluesky searches" style={input} />
            </View>
            <View style={{ gap: 6 }}>
                <Text style={label}>COMMUNITIES</Text>
                <Text style={{ color: AV.faint, fontSize: 11, lineHeight: 16 }}>Open: links and our own posts allowed. Thread-only: link only when the thread asks for a tool. Help-only: answer, no link. Banned: never touched.</Text>
                {s.communities.map((c, i) => (
                    <View key={`${c.platform}:${c.slug}`} style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <Text style={{ color: AV.ink, fontSize: 13, width: 170 }}>{c.platform === 'reddit' ? `r/${c.slug}` : `${PLATFORM_LABEL[c.platform]} ${c.slug}`}</Text>
                        <Segments value={c.promo} onChange={(promo) => set({ communities: s.communities.map((x, j) => (j === i ? { ...x, promo } : x)) })} options={['open', 'thread-only', 'help-only', 'banned'].map((v) => ({ value: v, label: v }))} />
                        <Btn small label={c.enabled ? 'On' : 'Off'} tone={c.enabled ? 'good' : 'default'} onPress={() => set({ communities: s.communities.map((x, j) => (j === i ? { ...x, enabled: !x.enabled } : x)) })} />
                    </View>
                ))}
                <AddCommunity onAdd={(c) => set({ communities: [...s.communities, c] })} />
            </View>
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}><Btn label={saving ? 'Saving…' : 'Save settings'} tone="primary" onPress={save} disabled={saving} /></View>
        </Panel>
    );
}

function AddCommunity({ onAdd }) {
    const [slug, setSlug] = useState('');
    return (
        <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <TextInput value={slug} onChangeText={setSlug} placeholder="Add subreddit, e.g. sportsbook" placeholderTextColor={A.faint} accessibilityLabel="Add subreddit" style={[input, { flex: 1, maxWidth: 280 }]} />
            <Btn small label="Add (help-only)" disabled={!slug.trim()} onPress={() => { onAdd({ platform: 'reddit', slug: slug.trim().replace(/^r\//i, ''), promo: 'help-only', enabled: true }); setSlug(''); }} />
        </View>
    );
}

export default function GrowthTab() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [running, setRunning] = useState(null);
    const [filter, setFilter] = useState('review');
    const [leadFilter, setLeadFilter] = useState('new');
    const [showSettings, setShowSettings] = useState(false);

    const load = useCallback(async () => {
        setError(null);
        try { setData(await getGrowthOverview()); } catch (e) { setError(e.message); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const act = async (item, action, payload = {}) => {
        setBusy(`${item.id}:${action}`);
        try {
            const r = await growthAction(action, { id: item.id, ...payload });
            if (action === 'draftLead' || action === 'redraft') showToast(r.skipped ? `Skipped: ${r.reason}` : `Drafted (${r.status})`, r.skipped ? 'info' : 'success');
            else if (action === 'edit') showToast(r.status === 'ready' ? 'Saved. Ready to approve.' : 'Saved, but it still needs fixes.', r.status === 'ready' ? 'success' : 'info');
            else if (action !== 'approve') showToast('Updated', 'success');
            await load();
            return true;
        } catch (e) { showToast(e.message, 'error'); return false; } finally { setBusy(null); }
    };
    const run = async (job) => {
        setRunning(job);
        try {
            const r = await runGrowthJob(job);
            showToast(job === 'discovery'
                ? `Radar: ${r.discovery.fetched} read, ${r.discovery.stored} new leads`
                : `Drafting: ${r.drafting.made} drafts${r.drafting.reason ? ` (${r.drafting.reason})` : ''}`, 'success');
            await load();
        } catch (e) { showToast(e.message, 'error'); } finally { setRunning(null); }
    };

    const drafts = useMemo(() => filterDrafts(data?.drafts || [], filter), [data, filter]);
    const counts = useMemo(() => draftCounts(data?.drafts || []), [data]);
    const leads = useMemo(() => (data?.leads || []).filter((l) => (leadFilter === 'all' ? true : leadFilter === 'new' ? ['new', 'skipped'].includes(l.status) : l.status === leadFilter))
        .sort((a, b) => b.score - a.score), [data, leadFilter]);

    if (error) return <EmptyState icon="!" title="Couldn't load Growth" body={`${error}. Deploy the growth functions (adminGrowth*) first.`} />;
    if (!data) return <View style={{ padding: 40, alignItems: 'center' }}><ActivityIndicator color={A.accent} /></View>;

    const { goal, funnel, today, budget, settings } = data;
    const spentToday = budget?.[0]?.costUsd || 0;
    const rows = sourceRows(data.sources);
    const dataMode = data.drafting?.dataMode || 'live';

    return (
        <ScrollView contentContainerStyle={{ padding: 16, gap: 16, paddingBottom: 60 }}>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
                <View style={{ flex: 1, minWidth: 260 }}>
                    <Text style={{ color: AV.ink, fontSize: 22, fontWeight: '800', ...ADM_DISPLAY }}>Growth</Text>
                    <Text style={{ color: AV.faint, fontSize: 12, marginTop: 2 }}>
                        Find. Draft. You approve. Nothing posts by itself. {data.drafting?.lastRunAt ? `Last drafting run ${relTime(data.drafting.lastRunAt)}.` : 'Drafting has not run yet.'}
                    </Text>
                </View>
                <Btn small label={running === 'discovery' ? 'Reading…' : 'Run radar'} onPress={() => run('discovery')} disabled={!!running} />
                <Btn small tone="primary" label={running === 'drafting' ? 'Drafting…' : 'Draft now'} onPress={() => run('drafting')} disabled={!!running} />
                <Btn small label={showSettings ? 'Hide settings' : 'Settings'} onPress={() => setShowSettings((x) => !x)} />
            </View>

            {!settings.enabled && <Banner>Radar and drafting are off. Turn them on in Settings.</Banner>}
            {DATA_MODE_COPY[dataMode] && <Banner tone="info">{DATA_MODE_COPY[dataMode]}</Banner>}
            {!data.aiKeyPresent && <Banner>No OpenAI key in this function. Drafts use templates.</Banner>}
            {data.aiKeyPresent && settings.ai.enabled && spentToday >= settings.ai.dailyBudgetUsd * 0.95 && <Banner>AI budget used for today ({money(spentToday)} of {money(settings.ai.dailyBudgetUsd)}). New drafts use templates until 00:00 UTC.</Banner>}

            <Panel title="Beta goal" subtitle={`Waitlist joins from growth links, ${shortDate(goal.startAt)} to ${shortDate(goal.endAt)}. Every link carries utm_medium=growth.`}>
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10 }}>
                    <Stat index={0} label="Growth joins" value={goal.count} hint={`of ${goal.target} · ${String(goal.status).replace('-', ' ')}`} tone={GOAL_TONE[goal.status]} />
                    <Stat index={1} label="Last 7 days" value={goal.last7} hint={`${goal.pacePerDay}/day`} />
                    <Stat index={2} label="Needed / day" value={goal.neededPerDay ?? '—'} hint={`${goal.daysLeft} days left`} />
                    <Stat index={3} label="Projected" value={goal.projected} hint={`on-pace line: ${goal.expected} by today`} />
                    <Stat index={4} label="Approved today" value={today.approved} hint={`target ${today.target} · max ${today.max}`} tone={today.approved >= today.target ? 'good' : undefined} />
                    <Stat index={5} label="AI spend today" value={money(spentToday)} hint={`${budget?.[0]?.calls || 0} calls · cap ${money(settings.ai.dailyBudgetUsd)}`} />
                </View>
            </Panel>

            <Panel title="Conversions" subtitle={`All waitlist: ${data.waitlistTotal ?? '—'}. Below: only people who arrived through a growth link.`}>
                <HBars showPct total={funnel.totals.joined || 1} rows={[
                    { step: 'Joined waitlist', count: funnel.totals.joined },
                    { step: 'Approved / invited', count: funnel.totals.approved },
                    { step: 'Signed up', count: funnel.totals.signedUp },
                    { step: 'Ran a search', count: funnel.totals.activated },
                ]} />
                {funnel.campaigns.length ? funnel.campaigns.slice(0, 12).map((c) => (
                    <View key={c.campaign} style={{ flexDirection: 'row', gap: 10, paddingVertical: 6, borderTopWidth: 1, borderTopColor: AV.line, flexWrap: 'wrap' }}>
                        <Text style={{ color: AV.ink, fontSize: 12, flex: 1, minWidth: 160 }} numberOfLines={1}>{c.campaign}</Text>
                        <Text style={{ color: AV.soft, fontSize: 12, fontFamily: A.mono }}>{c.joined} joined · {c.approved} approved · {c.signedUp} signed up · {c.activated} searched</Text>
                    </View>
                )) : <Text style={{ color: AV.faint, fontSize: 12 }}>No growth-link sign-ups yet. They appear here once a posted link brings someone to the waitlist.</Text>}
            </Panel>

            <Panel title="Review queue" subtitle="Every draft has the 21+ / 1-800-GAMBLER line and is checked for profit promises, tout language and each community's link rules. Flagged drafts need an edit before they can be approved.">
                <Segments value={filter} onChange={setFilter} options={DRAFT_FILTERS.map((f) => ({ value: f.value, label: `${f.label} ${counts[f.value] ?? ''}`.trim() }))} />
                {!drafts.length
                    ? <EmptyState title={filter === 'review' ? 'Nothing to review' : 'Nothing here'} body={filter === 'review' ? 'Drafting runs every 3 hours at :45 (UTC). Press "Draft now" to run it.' : undefined} />
                    : drafts.slice(0, 40).map((d, i) => <DraftCard key={d.id} d={d} busy={busy} act={act} index={i} />)}
            </Panel>

            <Panel
                title="Radar"
                subtitle="Threads scored 0–100: asks for a tool, names a competitor, line-shopping pain, fresh, few replies. Recovery / quit-gambling threads, giveaways and touts are never drafted."
                right={rows.length ? (
                    <View style={{ gap: 4, alignItems: 'flex-end' }}>
                        {rows.map((r) => <Pill key={r.name} label={`${PLATFORM_LABEL[r.name]}: ${r.state === 'ok' ? 'ok' : r.state}`} color={r.state === 'ok' ? A.green : r.state === 'blocked' ? A.amber : A.red} />)}
                    </View>
                ) : null}
            >
                {rows.filter((r) => r.state !== 'ok').map((r) => <Text key={r.name} style={{ color: AV.faint, fontSize: 12 }}>{PLATFORM_LABEL[r.name]}: {r.text}{r.lastOkAt ? ` (last ok ${relTime(r.lastOkAt)})` : ''}</Text>)}
                <Segments value={leadFilter} onChange={setLeadFilter} options={[{ value: 'new', label: 'New' }, { value: 'drafted', label: 'Drafted' }, { value: 'snoozed', label: 'Snoozed' }, { value: 'dismissed', label: 'Dismissed' }, { value: 'all', label: 'All' }]} />
                {!leads.length
                    ? <EmptyState title="No leads here" body="The radar reads Reddit and Bluesky every 3 hours at :20 (UTC). Add X, Discord or forum threads by hand below." />
                    : leads.slice(0, 50).map((l, i) => <LeadRow key={l.id} l={l} busy={busy} act={act} index={i} />)}
            </Panel>

            <Panel title="Add a lead by hand" subtitle="For X, Discord, forums, or any Reddit thread the radar missed. Paste the link and what they wrote; a reply is drafted into the queue.">
                <AddLead onAdded={load} />
            </Panel>

            {showSettings && <SettingsPanel settings={settings} onSaved={() => load()} />}
        </ScrollView>
    );
}
